import React, { useEffect, useState, useContext, useRef } from "react";
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Tab,
  Tabs,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { BrandContext } from "../../App";
import { ThumbUpOutlined } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import TranscriptDisplay from "../simple/TranscriptDisplay";
import LessonResourcesDisplay from "../simple/LessonResourcesDisplay";
import { SwipeableDrawer } from "@mui/material";
import { useAPI } from "../../hooks/useAPI";

import { useAuth } from "../../contexts/AuthContext";

const CallScreenSidePanel = ({
  scenarioData,
  transcript = null,
  tab,
  onClose,
  isExternalMode,
  isMobile,
  simulationType,
}) => {
  const theme = useTheme();
  const { brand } = useContext(BrandContext);
  const { authToken } = useAuth();

  const { callAPI } = useAPI();
  const [recommendations, setRecommendations] = useState([]);
  const [value, setValue] = useState(tab);
  const [drawerOpen, setDrawerOpen] = React.useState(true);
  const [drawerExpanded, setDrawerExpanded] = useState(false);
  const [drawerHeight, setDrawerHeight] = useState("50vh"); // default half screen
  const startY = useRef(0);
  const currentHeight = useRef(0);

  const isLandscapeMode = window.matchMedia("(orientation: landscape)").matches;

  const recommendationsHidden =
    isExternalMode || !scenarioData?.has_scoring_engine;
  const resourcesExist = scenarioData?.resources !== null;

  useEffect(() => {
    if (recommendationsHidden && tab === 1 && !resourcesExist) {
      setValue(0); // fallback
    } else {
      setValue(tab);
    }
  }, [tab, recommendationsHidden, resourcesExist]);

  useEffect(() => {
    if (recommendationsHidden) return;

    const loadSimulations = async () => {
      const scenarioId = scenarioData?.id;
      if (!scenarioId) return;

      const data = await callAPI(
        `recent_simulations_with_evaluation?scenario_id=${scenarioData?.id}`,
        {
          token: authToken,
        },
      );

      if (!Array.isArray(data) || data.length === 0) return;

      const latestValidSim = data
        .filter((sim) => sim.evaluation !== null)
        .sort((a, b) => new Date(b.ended_at) - new Date(a.ended_at))[0];

      if (!latestValidSim) return;

      const recs = [];
      for (const key in latestValidSim.evaluation) {
        const section = latestValidSim.evaluation[key];
        if (
          typeof section === "object" &&
          Array.isArray(section.recommendations)
        ) {
          recs.push(...section.recommendations);
        }
      }

      setRecommendations(recs);
    };

    loadSimulations();
  }, [scenarioData?.id, authToken, brand.api_base_url, recommendationsHidden]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleTouchStart = (e) => {
    startY.current = e.touches[0].clientY;
    currentHeight.current =
      drawerHeight === "100vh" ? window.innerHeight : window.innerHeight / 2;
  };

  const handleTouchMove = (e) => {
    const deltaY = e.touches[0].clientY - startY.current;
    let newHeight = currentHeight.current - deltaY;

    // clamp between 50vh and 100vh
    const minHeight = window.innerHeight / 2;
    const maxHeight = window.innerHeight;
    newHeight = Math.max(minHeight, Math.min(maxHeight, newHeight));

    setDrawerHeight(`${newHeight}px`);
  };

  const handleTouchEnd = () => {
    // snap to nearest state
    const half = window.innerHeight / 2;
    const full = window.innerHeight;
    const current = parseInt(drawerHeight);

    if (current > (half + full) / 2) {
      setDrawerHeight("100vh");
    } else {
      setDrawerHeight("50vh");
    }
  };

  const SidePanelContent = () => {
    return (
      <>
        <Tabs
          value={value}
          onChange={handleChange}
          TabIndicatorProps={{ style: { display: "none" } }}
          textColor="inherit"
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            padding: 0,
            mb: theme.customSpacing["sm"],
            gap: theme.customSpacing["2xl"],
            backgroundColor: "transparent",
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: theme.typography.fontWeightMedium,
              fontSize: theme.typography.textSM.fontSize,
              color: theme.palette.text.disabled,
              padding: `${theme.customSpacing.sm} ${theme.customSpacing.lg}`,
              borderRadius: "4px",
              minHeight: "unset",
              minWidth: "unset",
            },
            "& .Mui-selected": {
              backgroundColor: theme.palette.foreground.button_secondary,
              color: theme.palette.text.brand_primary,
              fontWeight: theme.typography.fontWeightRegular,
            },
          }}
        >
          <Tab label="Transcript" />
          {!recommendationsHidden && <Tab label="Recommendations" />}
          {resourcesExist && simulationType !== "on_demand_coach" && simulationType !== "marketplace_demo" && (
            <Tab label="Resources" />
          )}
        </Tabs>
        <Box
          sx={{
            position: "absolute",
            top: 30,
            right: 12,
            zIndex: 999,
            color: "black",
          }}
        >
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider
          sx={{
            marginX: `-${theme.customSpacing["3xl"]}`,
            marginBottom: theme.customSpacing["md"],
          }}
        />
        {value === 0 && (
          <TranscriptDisplay
            transcript={transcript}
            scenarioData={scenarioData}
          />
        )}
        {value === 1 &&
        !recommendationsHidden &&
        simulationType !== "on_demand_coach" ? (
          <List dense sx={{ flexGrow: 1, overflowY: "auto" }}>
            {recommendations.length > 0 ? (
              recommendations.map((item, index) => (
                <ListItem
                  key={`recommendation-${index}`}
                  alignItems="flex-start"
                  sx={{ paddingY: 0.5, margin: 0, padding: 0, mb: 5 }}
                >
                  <ListItemAvatar>
                    <Avatar
                      variant="rounded"
                      sx={{
                        backgroundColor:
                          theme.palette.background.button_primary,
                        border: `1px solid #18C9D8`,
                        color: theme.palette.background.primary,
                        width: 26,
                        height: 26,
                      }}
                    >
                      <ThumbUpOutlined
                        sx={{ fontSize: theme.typography.textMD.fontSize }}
                      />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primaryTypographyProps={{
                      fontSize: theme.typography.textMD.fontSize,
                      lineHeight: theme.typography.textMD.lineHeight,
                      fontWeight: theme.typography.fontWeightLight,
                      color: theme.palette.background.brand_alt,
                    }}
                    primary={item}
                  />
                </ListItem>
              ))
            ) : (
              <Box
                sx={{
                  padding: theme.customSpacing.md,
                  color: theme.palette.text.secondary,
                }}
              >
                No recommendations available.
              </Box>
            )}
          </List>
        ) : (
          // Use this block when recommendations are hidden
          value === 1 &&
          resourcesExist && (
            <Box
              sx={{
                height: "100%",
                width: "100%",
                display: "flex",
                overflowY: "auto",
              }}
            >
              <LessonResourcesDisplay
                resources={scenarioData?.resources}
                backgroundMode="light"
              />
            </Box>
          )
        )}
      </>
    );
  };

  return !isMobile ? (
    <Box
      sx={{
        position: "relative",
        width: "408px",
        height: "98%",
        backgroundColor: "white",
        boxShadow: "0px 0px 10px rgba(0,0,0,0.1)",
        display: "flex",
        flexDirection: "column",
        padding: theme.customSpacing["3xl"],
        borderRadius: theme.shape.radius,
        touchAction: "none",
      }}
    >
      <SidePanelContent />
    </Box>
  ) : (
    <SwipeableDrawer
      anchor={isLandscapeMode ? "right" : "bottom"}
      open={drawerOpen}
      onOpen={() => setDrawerOpen(true)}
      onClose={() => {
        setDrawerOpen(false);
        setDrawerHeight("50vh");
        onClose();
      }}
      hideBackdrop
      PaperProps={{
        sx: {
          height: !isLandscapeMode ? drawerHeight : "100vh",
          borderTopLeftRadius: !isLandscapeMode ? 12 : 0,
          borderTopRightRadius: !isLandscapeMode ? 12 : 0,
          transition: "height 0.2s ease",
          padding: "20px",
          width: !isLandscapeMode ? "100vw" : "50vw",
        },
      }}
    >
      {/* Grab handle */}
      {!isLandscapeMode && (
        <Box
          sx={{
            position: "absolute",
            top: 8,
            left: "50%",
            transform: "translateX(-50%)",
            opacity: "0.4",
            cursor: "grab",
            zIndex: 10,
            touchAction: "none",
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <Box
            sx={{
              width: 134,
              height: 8,
              borderRadius: 1000,
              margin: "0 auto",
              bgcolor: "#A4A7AE",
            }}
          />
        </Box>
      )}
      <SidePanelContent />
    </SwipeableDrawer>
  );
};

export default CallScreenSidePanel;